import React, { useState } from 'react';
import { StickyNote, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { formatApiError } from '@/utils/apiError';

const STATUS = [['not_contacted', 'Not contacted', 'bg-gray-100 text-gray-700'], ['contacted', 'Contacted', 'bg-sky-100 text-sky-800'], ['quoted', 'Quoted', 'bg-amber-100 text-amber-800'], ['sold', 'Sold', 'bg-emerald-100 text-emerald-800'], ['not_interested', 'Not interested', 'bg-rose-50 text-rose-700']];

/** Website-sale status + notes for one installer (staff only). */
export function UpsellCell({ row, onSaved }) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(row.notes || '');
  const [next, setNext] = useState(row.next_action_date || '');
  const [busy, setBusy] = useState(false);

  const save = async (patch) => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/installer-checklist/${row.user_id}/upsell`, { method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Save failed');
      onSaved({ website_status: d.website_status || row.website_status, notes: d.notes ?? row.notes, next_action_date: d.next_action_date ?? null, updated_by: d.updated_by, updated_at: d.updated_at });
      toast.success('Saved');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const cls = STATUS.find((s) => s[0] === row.website_status)?.[2] || STATUS[0][2];

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <select value={row.website_status} onChange={(e) => save({ website_status: e.target.value })} disabled={busy} className={`h-8 rounded-full px-2.5 text-xs font-semibold border-0 ${cls}`} data-testid={`upsell-status-${row.user_id}`}>
          {STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button type="button" onClick={() => setOpen((o) => !o)} className={`p-1.5 rounded hover:bg-gray-100 ${row.notes ? 'text-amber-600' : 'text-gray-400'}`} title={row.notes || 'Add a note'} aria-label="Notes" data-testid={`upsell-notes-toggle-${row.user_id}`}><StickyNote className="w-4 h-4" /></button>
        {busy && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
      </div>
      {row.next_action_date && !open && <div className="text-[10px] text-gray-500">Follow up {row.next_action_date}</div>}
      {row.updated_by && !open && <div className="text-[10px] text-gray-400 truncate">by {row.updated_by}</div>}
      {open && (
        <div className="space-y-1.5 bg-white border rounded-lg p-2" data-testid={`upsell-notes-${row.user_id}`}>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Spoke to Dave — wants a 5-page site, send quote Friday" className="text-xs" data-testid={`upsell-notes-input-${row.user_id}`} />
          <div className="flex items-center gap-1.5">
            <Input type="date" value={next} onChange={(e) => setNext(e.target.value)} className="h-8 text-xs w-36" data-testid={`upsell-next-${row.user_id}`} />
            <Button size="sm" className="h-8 ml-auto bg-[#3b1f5e] hover:bg-[#2a1544] text-white" disabled={busy} onClick={async () => { await save({ notes, next_action_date: next || '' }); setOpen(false); }} data-testid={`upsell-notes-save-${row.user_id}`}>Save</Button>
          </div>
        </div>
      )}
    </div>
  );
}
